import { useFeature } from "@growthbook/growthbook-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useRouteMatch } from "react-router-dom";

import {
  NavBarItem,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";

import {
  trackLaunchTrekButtonClick,
  trackOutcomesHomeSelected,
  trackView,
} from "@lib/event-trackers";
import { hasAnyProducts } from "@lib/product.utils";
import {
  ActivityPlayerRoute,
  ClassroomRoute,
  CurricularRoute,
  CurriculumResourcesRoute,
  DevicesRoute,
  FundingRoute,
  IntegrationsRoute,
  MultiOrgDashboardRoute,
  MyWorkRoute,
  OrgsRoute,
  ProductCatalogRoute,
  ProgramsRoute,
  RootPath,
  SingleOrgDashboardRoute,
  SupportRoute,
  TrekRoute,
  UsersRoute,
  isEntryPointPath,
} from "@lib/routes.config";
import { Role, hasRole } from "@models";
import { XAPI_NAV_NAMES } from "@models/tracking/event-types";
import {
  ViewCatalog,
  ViewClassroomList,
  ViewOrganizationList,
  ViewSupport,
} from "@models/tracking/events";
import { ClickNavbarItemEvent } from "@models/tracking/xapi-events";
import {
  useCurrentUser,
  useUserClassroomCourses,
} from "@src/hooks/queries/account";
import {
  useFeatures,
  useIntegrationOrgCodes,
  useRole,
  useUserClassroomProducts,
} from "@src/hooks/queries/account-selectors";
import { useOrganizationProgramsEnabled } from "@src/hooks/queries/programs";
import {
  useClassrooms,
  useDashboardConfigs,
  useIntegrations,
  useOrganizationDetails,
  useOrganizationDeviceCount,
  useOrganizationUsers,
  useOrganizations,
} from "@src/hooks/queries/organization";
import { useAppStore } from "@src/stores/zustand/app-store";
import { useQueryState } from "nuqs";

import container from "@src/container";

import { previewParser } from "@lib/utils/search-params-parsers";
import "./navbar.routes.scss";

export function NavbarRoutes({ addNotification, isExpanded, mode = "full" }) {
  const { url } = useRouteMatch(RootPath);
  const { accountService, xApiTrackingService } = container;
  const role = useRole();
  const features = useFeatures();
  const currentUser = useCurrentUser();
  const userClassroomProducts = useUserClassroomProducts();

  const userId = currentUser?.userId;
  const organizationCode = currentUser?.organizationCode;

  const { integrationOrgCodes } = useIntegrationOrgCodes(organizationCode);

  const { data: organizationDetails } = useOrganizationDetails(
    organizationCode,
    {
      enabled: !!organizationCode,
    }
  );

  const showIntegrationsTab =
    Boolean(features?.integrationsTabEnabled) &&
    (Boolean(organizationDetails?.integrations) ||
      integrationOrgCodes.size > 0);

  const { data: organizations } = useOrganizations(organizationCode, {
    enabled:
      !!organizationCode &&
      (role === Role.MULTI_ORG_ADMIN.id || role === Role.MULTI_ORG_VIEWER.id),
  });
  const isStudent = role === Role.STUDENT.id;

  const { data: programsEnabled } = useOrganizationProgramsEnabled({
    enabled: !!organizationCode && !isStudent,
  });
  const showProgramsTab =
    programsEnabled === true && hasRole(ProgramsRoute.roles, role);

  const { data: classrooms } = useClassrooms(
    { userId, role, orgCode: organizationCode },
    { enabled: !!(userId && role && organizationCode) && !isStudent }
  );
  const { data: users } = useOrganizationUsers(
    { role, orgCode: organizationCode, userId },
    { enabled: !!(role && organizationCode && userId) && !isStudent }
  );
  const { data: integrationsList } = useIntegrations(organizationCode, {
    enabled: !!organizationCode && !isStudent,
  });
  const { data: dashboardConfigs } = useDashboardConfigs({
    enabled: !isStudent,
  });

  const { data: userClassroomCourses } = useUserClassroomCourses(userId, {
    enabled: !!userId,
  });

  const lastEntryPoint = useAppStore((state) => state.lastEntryPoint);
  const setLastEntryPoint = useAppStore((state) => state.setLastEntryPoint);

  const orgsCount = organizations?.length || 0;

  const { data: deviceCount } = useOrganizationDeviceCount(organizationCode, {
    enabled: !!organizationCode && !isStudent,
  });

  const location = useLocation();

  const [gettingTrekSession, setGettingTrekSession] = useState(false);

  const trackClickEvent = (name) => () => {
    xApiTrackingService.trackEvent(new ClickNavbarItemEvent(name));
  };

  const curriculumResourcesAvailable = useMemo(
    () =>
      hasAnyProducts(currentUser?.products, [
        Product.TRK,
        Product.CE,
        Product.TS,
        Product.PROG,
      ]),
    [currentUser]
  );

  useEffect(() => {
    const path = isEntryPointPath(location.pathname)
      ? location.pathname
      : lastEntryPoint;
    setLastEntryPoint(path);
  }, [location]);

  const clearPreviewClassroomId = useAppStore(
    (state) => state.clearPreviewClassroomId
  );
  const [previewClassroomId, setPreview] = useQueryState(
    "preview",
    previewParser
  );

  useEffect(() => {
    const isPreviewSafeRoute =
      (location.pathname.includes(MyWorkRoute.path) && !!previewClassroomId) ||
      location.pathname.includes(ActivityPlayerRoute.path);

    if (!isPreviewSafeRoute) {
      clearPreviewClassroomId();
    }
    if (!isPreviewSafeRoute && previewClassroomId) {
      setPreview(null);
    }
  }, [location.pathname, previewClassroomId, clearPreviewClassroomId]);

  useEffect(() => {
    const trackingEventsByRoute = {
      [OrgsRoute.path]: { event: ViewOrganizationList, label: OrgsRoute.label },
      [ClassroomRoute.path]: {
        event: ViewClassroomList,
        label: ClassroomRoute.label,
      },
    };

    const routeName = location.pathname.replace(/^\/app/, "");
    const trackingData = trackingEventsByRoute[routeName];

    trackingData && trackView(trackingData.event, trackingData.label);
  }, [location]);

  const dashboardRoute = hasRole(MultiOrgDashboardRoute.roles, role)
    ? MultiOrgDashboardRoute
    : SingleOrgDashboardRoute;

  // The dashboard-home event belongs to the outcomes dictionary, so it is only
  // reported where this item lands on the outcomes dashboard.
  const outcomeReportingEnabled = useFeature("outcome-reporting.enabled").on;

  //Encrypted userid
  const id = window.btoa(userId);

  const dashboardNavItem = (
    <NavBarItem
      to={`${url}${dashboardRoute.path}`}
      isExpanded={isExpanded}
      isActiveRoute={lastEntryPoint?.includes(dashboardRoute.path)}
      icon={dashboardRoute.icon}
      label={dashboardRoute.label}
      exact={true}
      routerComponent={NavLink}
      onClick={
        outcomeReportingEnabled && !isStudent
          ? () => trackOutcomesHomeSelected("outcomes_dashboard_home")
          : undefined
      }
    ></NavBarItem>
  );

  const orgsNavItem = hasRole(OrgsRoute.roles, role) && (
    <NavBarItem
      to={`${url}${OrgsRoute.path}`}
      isExpanded={isExpanded}
      isActiveRoute={lastEntryPoint?.includes(OrgsRoute.path)}
      icon={OrgsRoute.icon}
      label={OrgsRoute.label}
      labelAbbr={OrgsRoute.labelAbbr}
      exact={true}
      badge={orgsCount || undefined}
      routerComponent={NavLink}
    ></NavBarItem>
  );

  const classroomsNavItem = (
    <NavBarItem
      to={`${url}${ClassroomRoute.path}`}
      isExpanded={isExpanded}
      isActiveRoute={lastEntryPoint?.includes(ClassroomRoute.path)}
      icon={ClassroomRoute.icon}
      label={ClassroomRoute.label}
      exact={true}
      badge={classrooms?.length}
      routerComponent={NavLink}
    ></NavBarItem>
  );

  const programsNavItem = (
    <NavBarItem
      to={`${url}${ProgramsRoute.path}`}
      isExpanded={isExpanded}
      isActiveRoute={lastEntryPoint?.includes(ProgramsRoute.path)}
      icon={ProgramsRoute.icon}
      label={ProgramsRoute.label}
      exact={true}
      routerComponent={NavLink}
    ></NavBarItem>
  );

  const usersNavItem = (
    <NavBarItem
      to={`${url}${UsersRoute.path}`}
      isExpanded={isExpanded}
      isActiveRoute={lastEntryPoint?.includes(UsersRoute.path)}
      icon={UsersRoute.icon}
      label={UsersRoute.label}
      exact={true}
      // TODO: Find a way to update this count without doing a double api call (navbar and users component)
      // Maybe passing the users to the component?
      badge={users?.length}
      routerComponent={NavLink}
    ></NavBarItem>
  );

  const devicesNavItem = (deviceCount ?? 0) > 0 &&
    hasRole(DevicesRoute.roles, role) && (
      <NavBarItem
        to={`${url}${DevicesRoute.path}`}
        isActiveRoute={lastEntryPoint?.includes(DevicesRoute.path)}
        icon={DevicesRoute.icon}
        exact={true}
        label={DevicesRoute.label}
        routerComponent={NavLink}
        onClick={trackClickEvent(XAPI_NAV_NAMES.VIEW_HEADSET_DEVICES)}
      ></NavBarItem>
    );

  const integrationsNavItem = (
    <NavBarItem
      to={`${url}${IntegrationsRoute.path}`}
      isExpanded={isExpanded}
      isActiveRoute={lastEntryPoint?.includes(IntegrationsRoute.path)}
      icon={IntegrationsRoute.icon}
      label={IntegrationsRoute.label}
      exact={true}
      badge={integrationsList?.length}
      routerComponent={NavLink}
    ></NavBarItem>
  );

  const standardsNavItem = (
    <NavBarItem
      href={CurricularRoute.path}
      isExpanded={isExpanded}
      icon="circle-nodes"
      label={CurricularRoute.label}
      labelAbbr={CurricularRoute.labelAbbr}
    ></NavBarItem>
  );

  const productCatalogNavItem = (
    <NavBarItem
      href={dashboardConfigs?.productCatalogUrl}
      isExpanded={isExpanded}
      icon="books"
      label={ProductCatalogRoute.label}
      labelAbbr={ProductCatalogRoute.labelAbbr}
      onClick={() => {
        trackView(ViewCatalog, ProductCatalogRoute.label);
      }}
    ></NavBarItem>
  );

  const supportNavItem = (
    <NavBarItem
      href={SupportRoute.path}
      isExpanded={isExpanded}
      icon="question-circle"
      label={SupportRoute.label}
      labelAbbr={SupportRoute.labelAbbr}
      onClick={() => {
        trackView(ViewSupport, SupportRoute.label);
      }}
    ></NavBarItem>
  );

  const fundingNavItem = (
    <NavBarItem
      href={`${FundingRoute.path}`}
      isExpanded={isExpanded}
      icon="money-check-dollar-pen"
      label={FundingRoute.label}
      labelAbbr={FundingRoute.labelAbbr}
    ></NavBarItem>
  );

  const myWorkNavItem = (
    <NavBarItem
      to={`${url}${MyWorkRoute.path}`}
      isExpanded={isExpanded}
      isActiveRoute={lastEntryPoint?.includes(MyWorkRoute.path)}
      icon={MyWorkRoute.icon}
      label={MyWorkRoute.label}
      exact={true}
      routerComponent={NavLink}
    ></NavBarItem>
  );

  const trekNavItem = (
    <div className={clsx("trek-nav-item", { loading: gettingTrekSession })}>
      <NavBarItem
        href="#"
        isExpanded={isExpanded}
        icon={TrekRoute.icon}
        label={TrekRoute.label}
        onClick={async (e) => {
          e.preventDefault();
          if (gettingTrekSession) {
            return;
          }

          trackLaunchTrekButtonClick();
          // Open synchronously before any await so Safari's popup blocker
          // recognizes this as a direct user gesture.
          const trekWindow = window.open("", "_blank");
          try {
            setGettingTrekSession(true);
            const { session_id: sessionId } =
              await accountService.ssoTransfer();
            trekWindow.location.href = `${TrekRoute.path}?sessionId=${sessionId}`;
          } catch {
            trekWindow.close();
            addNotification({
              type: NotificationType.error,
              message: (
                <>
                  <div style={{ fontWeight: "600" }}>Trek SSO Problem</div>
                  <div>
                    There was an issue launching Trek from your account. Please
                    try again.
                  </div>
                </>
              ),
            });
          } finally {
            setGettingTrekSession(false);
          }
        }}
      />
      {gettingTrekSession ? (
        <FontAwesomeIcon icon="fa-solid fa-spinner" spin />
      ) : (
        !isExpanded && (
          <FontAwesomeIcon icon="fa-regular fa-arrow-up-right-from-square" />
        )
      )}
    </div>
  );

  const curriculumResourcesNavItem = (
    <NavBarItem
      to={`${url}${CurriculumResourcesRoute.path}`}
      isExpanded={isExpanded}
      isActiveRoute={lastEntryPoint?.includes(CurriculumResourcesRoute.path)}
      icon={CurriculumResourcesRoute.icon}
      label={CurriculumResourcesRoute.label}
      labelAbbr={CurriculumResourcesRoute.labelAbbr}
      exact={true}
      routerComponent={NavLink}
      onClick={trackClickEvent(XAPI_NAV_NAMES.CURRICULUM_PLANNER)}
    ></NavBarItem>
  );
  if (role === Role.STUDENT.id) {
    return (
      <>
        {myWorkNavItem}
        {userClassroomProducts.has(Product.TRK) && trekNavItem}
      </>
    );
  } else {
    switch (mode) {
      case "full": {
        return (
          <>
            {dashboardNavItem}
            {orgsNavItem}
            {classroomsNavItem}
            {showProgramsTab && programsNavItem}
            {usersNavItem}
            {[Role.INSTRUCTOR.id, Role.ORG_ADMIN.id].includes(role) &&
              organizationDetails?.professionalLearning &&
              userClassroomCourses?.length > 0 &&
              myWorkNavItem}
            {devicesNavItem}
            {showIntegrationsTab && integrationsNavItem}
            {curriculumResourcesAvailable && curriculumResourcesNavItem}
            {standardsNavItem}
            {productCatalogNavItem}
            {supportNavItem}
            {/* {fundingNavItem} */}
          </>
        );
      }
      case "mobile": {
        return (
          <>
            {dashboardNavItem}
            {orgsNavItem}
            {classroomsNavItem}
            {showProgramsTab && programsNavItem}
            {usersNavItem}
            {showIntegrationsTab && integrationsNavItem}
          </>
        );
      }
      case "more": {
        return (
          <>
            {curriculumResourcesAvailable && curriculumResourcesNavItem}
            {standardsNavItem}
            {productCatalogNavItem}
            {supportNavItem}
            {fundingNavItem}
          </>
        );
      }
    }
  }
}

NavbarRoutes.propTypes = {
  addNotification: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  mode: PropTypes.string,
};
